/**
 * `F4` · El alta del comensal — [[ADR-002]].
 *
 * En su propio módulo y no en `menu.ts`: es del **comensal**, no de la carta, y
 * `menu.ts` está congelado salvo para añadir por una razón que aquí no aplica.
 */
import { z } from 'zod';
/**
 * Lo que manda el navegador para unirse.
 *
 * > [!danger] El teléfono NO viaja aquí, y es la decisión entera
 * > Llega dentro del `token` que Supabase firmó al verificar el código, y el
 * > servidor lo saca de ahí. Si viajara en el cuerpo, quien llamara podría poner
 * > el de otra persona — y el alta no tendría forma de notarlo, porque un
 * > teléfono es una cadena.
 */
export const esquemaInscripcion = z.object({
    /** El de Supabase, de verificar el código. Prueba de que el teléfono es suyo. */
    token: z.string().min(10).max(4096),
    /**
     * Cómo quiere que le llamen en el mostrador. Opcional a propósito: pedirle el
     * nombre a alguien que sólo quiere sus sellos es un campo más entre él y el
     * café, y `customers.name` es nula en la base desde la 004.
     */
    nombre: z.string().trim().min(2).max(80).optional(),
}).strict();
/** Lo que devuelve el alta: lo justo para pintar el encabezado. */
export const esquemaTarjeta = z.object({
    nombre: z.string().nullable(),
    /** `true` si esta cafetería ya lo tenía — «bienvenida de vuelta». */
    yaEstaba: z.boolean(),
}).strict();
/**
 * `F4` · Lo que devuelve `GET /publico/cafeterias/:slug/tarjeta`.
 *
 * Es lo que el encabezado del menú lee para saber a quién tiene delante, y lo
 * que dice es **su tarjeta en ESTA cafetería** y no si existe como cliente. Una
 * persona es global —`R-12`— y puede tener tarjeta en tres negocios; confundir
 * las dos cosas saludaría a alguien en un local donde nunca ha estado.
 *
 * Por eso `nombre` sólo viaja con `miembro` en cierto: sin tarjeta aquí no hay
 * nombre que poner en ella, y así los dos campos no pueden contradecirse.
 */
/**
 * `F5` · `R-05` — un premio que ya se ganó y está esperando en el mostrador.
 *
 * `venceEn` nulo quiere decir **no vence nunca**, que según la 063 es lo que va
 * a elegir casi toda cafetería. No es «no se sabe»: es una promesa sin fecha.
 */
export const esquemaPremioPendiente = z.object({
    premio: z.string().min(1),
    venceEn: z.iso.datetime().nullable(),
}).strict();
/**
 * `F5` · `T-57` · `R-03` — **cuántos sellos lleva** quien mira su tarjeta.
 *
 * Los tres viajan juntos porque los tres se pintan en la misma frase —«9 de 10
 * para Un café del mismo tamaño»— y porque separarlos dejaría que las dos
 * mitades se contradijeran: el saldo de una versión del programa contra el
 * objetivo de otra, por poco que durara la ventana entre dos peticiones.
 *
 * `lleva` puede ser `0` —el recién inscrito— y eso no es lo mismo que no tener
 * tarjeta; para eso está el nulo del campo entero.
 */
export const esquemaSellosDeLaTarjeta = z.object({
    /**
     * Los que tiene contados hoy. Cero el día que se une.
     *
     * > [!warning] Puede venir NEGATIVO, y no es un error del motor
     * > `R-10` deja que el dueño reste a mano —hasta −50— y esa resta cae después
     * > de que el premio ya se cobró. Un «−3» es un saldo real: alguien gastó más
     * > de lo que tenía, con motivo escrito y eslabón de auditoría.
     * >
     * > Se admite aquí, sin `nonnegative()`, **porque el número tiene que ser
     * > verdad**: el panel del dueño hace cuentas con él y el mostrador decide
     * > con él. Recortarlo en el contrato haría que dos capas vieran dos saldos
     * > distintos, que es peor que enseñar un menos.
     * >
     * > Quien recorta es la PANTALLA, y en un solo sitio: `sellosLlenos()` del
     * > menú acota a `[0, de]` para pintar la rejilla. Una casilla negativa no
     * > existe, pero el número sí.
     * >
     * > Lo destapó una prueba de `T-56` al ponerse roja: una fixtura con un premio
     * > de diez y ninguna visita da −10, y el esquema lo rechazó.
     */
    lleva: z.number().int(),
    /** Los que pide el programa vigente. Nunca cero: la 063 lo exige positivo. */
    de: z.number().int().positive(),
    /** Lo que se lleva al completarlos. */
    premio: z.string().min(1),
}).strict();
export const esquemaTarjetaVigente = z.object({
    nombre: z.string().nullable(),
    /** `false` sin cookie, con una que no vale, o con la de otra cafetería. */
    miembro: z.boolean(),
    /**
     * `F5` · `R-05` · Los premios que tiene **pendientes en esta cafetería**, del
     * que antes vence al que más tarda. Vacío es el caso normal.
     *
     * Lo que llega aquí ya está filtrado por el motor: la policy de la 070 deja
     * fuera los canjeados **y los caducados**, así que la pantalla no tiene que
     * acordarse de comprobar ninguna de las dos cosas. Sin eso enseñaría un
     * premio que la caja va a rechazar, porque el barrido que pone `vencido`
     * todavía no existe.
     */
    premios: z.array(esquemaPremioPendiente),
    /**
     * `F5` · `T-57` · `R-03` · **Cuántos sellos lleva, y de cuántos.** `null`
     * cuando no es miembro de esta cafetería, o cuando aquí no hay programa
     * vigente que contar.
     *
     * > [!danger] Sí, `de` y `premio` repiten `sellosPorPremio` y `premio` de
     * > `data.lealtad`, y NO es duplicación por descuido
     * > Los dos números son el mismo cuando los dos viajan, así que quien lea
     * > esto va a querer «arreglarlo» borrando uno. Que se encuentre el motivo:
     * >
     * > `data.lealtad` sale de `programa_publico()` (071), que está condicionada
     * > a `tenant_modules.status = 'active'`. Esto sale de `sellos_del_comensal()`
     * > (073), que **no lo está, a propósito**. En una cafetería **suspendida**
     * > `data.lealtad` viene nula y esto no.
     * >
     * > La asimetría es `ADR-011` —«suspender detiene el sellado, no el canje»—
     * > partida en sus dos mitades: `data.lealtad` es **la invitación** a quien
     * > todavía no está dentro, y por eso se apaga, porque invitar contrae
     * > obligación nueva. Esto es **la tarjeta de quien ya es miembro**, o sea
     * > leer una obligación que el negocio ya contrajo, y eso no se apaga. Unos
     * > sellos que desaparecen del menú son además el aviso de la deuda dicho en
     * > la peor forma posible: el comensal no concluye «esta cafetería no pagó»,
     * > concluye «me robaron mis sellos».
     * >
     * > Colapsarlos en uno haría que la tarjeta de un miembro de una cafetería
     * > suspendida se quedara sin objetivo ni premio que enseñar — que es
     * > exactamente lo que la 073 existe para impedir.
     */
    sellos: esquemaSellosDeLaTarjeta.nullable(),
    /**
     * `10-C` · Desde cuándo es miembro **de esta cafetería**. `null` si no lo es.
     *
     * Es `memberships.joined_at`, no `customers.created_at`: lo que la pantalla
     * dice es «cliente desde marzo 2026» dentro del menú de UN negocio, y quien
     * lleva dos años con FialRest puede haberse unido a éste la semana pasada.
     * Poner la fecha global sería contarle a esta cafetería desde cuándo usa la
     * tarjeta en otras — lo que el aviso de privacidad promete que no hacemos.
     *
     * ISO con `Z`, formateado en SQL como `premios[].venceEn` y por el mismo
     * motivo: el conector devuelve `timestamptz` como cadena, no como `Date`.
     */
    miembroDesde: z.iso.datetime().nullable(),
    /**
     * `10-C` · Su historia en esta cafetería. `null` si no es miembro.
     *
     * Los tres juntos porque se pintan juntos y porque salen de la misma función
     * —`resumen_del_comensal()`, la 074—: separarlos serían tres viajes para
     * llenar un recuadro.
     */
    historia: z.object({
        /**
         * Cuántas veces ha venido. **Sólo visitas selladas**: un ajuste de `R-10` no
         * es una visita, porque nadie entró por la puerta. Por eso este número y el
         * de `sellos.lleva` no tienen por qué cuadrar — son dos preguntas distintas
         * y el motor las contesta por separado a propósito.
         */
        visitas: z.number().int().nonnegative(),
        /** Premios que ya se llevó. La tarjeta sólo enseña los pendientes. */
        canjeados: z.number().int().nonnegative(),
        /**
         * `R-02` · A partir de cuándo se le puede volver a sellar. **`null` quiere
         * decir «ahora mismo»**, que es el caso de casi siempre — no «no se sabe».
         * Existe para que la pantalla diga «a partir de las 16:00» en vez de dejar
         * que alguien haga el viaje al mostrador para nada.
         */
        puedeSellarA: z.iso.datetime().nullable(),
    }).strict().nullable(),
}).strict();
/**
 * `F4` · El aviso de privacidad vigente — `GET /publico/aviso-de-privacidad`.
 *
 * **Nulo cuando no hay ninguno**, y eso no es un fallo: es el freno que la
 * migración 056 dejó puesto a propósito. Mientras el texto no esté aprobado, el
 * alta contesta `409` — y la pantalla, viendo este nulo, ni siquiera invita.
 * Un botón que lleva a «todavía no» delante de alguien sentado en una mesa es
 * peor que no ofrecer nada.
 *
 * Viaja **el texto entero** y no un enlace. Lo que la evidencia guarda es «la
 * versión que se le mostró», y eso sólo es verdad si de verdad estaba delante:
 * con un enlace, lo que quedaría escrito es que tuvo la oportunidad de leerlo.
 */
export const esquemaAvisoVigente = z.object({
    version: z.string().regex(/^v[0-9]+$/),
    texto: z.string().min(1),
}).strict().nullable();
/**
 * Lo que contesta la ruta, **envuelto en un objeto a propósito**.
 *
 * Un `null` devuelto a secas por un controlador de Nest no viaja como `null`:
 * viaja como un cuerpo **vacío**, y el cliente —que hace `response.json()`— se
 * encuentra con un error de parseo en el caso más corriente que hay. Medido, no
 * supuesto. Con el nulo dentro de un objeto, la respuesta siempre es JSON y el
 * «todavía no hay aviso» llega como lo que es.
 */
export const esquemaRespuestaDeAviso = z.object({
    aviso: esquemaAvisoVigente,
}).strict();
/**
 * `F4` · Lo que devuelve `POST /publico/cafeterias/:slug/codigo`.
 *
 * Los seis caracteres que el comensal enseña, y **cuánto viven**. Los dos hacen
 * falta: sin el segundo, la pantalla no puede enseñar la cuenta atrás y alguien
 * acabaría enseñando un código muerto a una cajera que no entiende por qué no
 * funciona.
 *
 * > [!danger] Es una DURACIÓN y no un instante, y eso no es un detalle
 * > La primera versión devolvía `expiraEn` en milisegundos desde la época, y el
 * > `check` de la 058 la rechazó en la primera prueba: la API calculaba el
 * > instante con **su** reloj y el motor lo comparaba contra `created_at`, que
 * > es el de **Postgres**. Son dos máquinas. En pruebas fallaba por
 * > milisegundos; en producción habría fallado por lo que hubiera de deriva
 * > entre Render y Supabase, que nadie mide.
 * >
 * > Con una duración no hay dos relojes que cuadrar: el motor pone la caducidad
 * > real —`now() + 3 minutos`, suyo de principio a fin— y el navegador cuenta
 * > hacia atrás desde que recibe la respuesta, con el suyo. La cuenta del
 * > navegador termina un pelo **después** que la del servidor, que es el lado
 * > seguro: la pantalla avisa de que caducó antes de que el mostrador lo
 * > rechace, no al revés.
 */
export const esquemaCodigoDeTarjeta = z.object({
    codigo: z.string(),
    /** Segundos desde que se recibe. La caducidad de verdad la lleva el motor. */
    viveSegundos: z.number().int().positive(),
}).strict();
//# sourceMappingURL=inscripcion.js.map